import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useUpdateCustomerContact from '@/api/mutations/customers/useUpdateCustomerContacts';
import complianceKeys from '@/api/queries/compliance/keyFactory';
import projectKeys from '@/api/queries/project/keyFactory';
import {
  ProjectByIdQuery,
  UpdateCustomerContactInput,
  UpdateCustomerContactMutation as _UpdateCustomerContactMutation,
} from '@/gql/graphql';

const useOptimisticUpdateCustomerContactOnProject = (
  projectId: string,
  options?: UseMutationOptions<
    _UpdateCustomerContactMutation,
    unknown,
    UpdateCustomerContactInput
  >
) => {
  const queryClient = useQueryClient();

  return useUpdateCustomerContact({
    ...options,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: projectKeys.overview(projectId),
      });

      // Snapshot the previous value
      const previousProjectOverview = queryClient.getQueryData(
        projectKeys.overview(projectId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<ProjectByIdQuery>(
        projectKeys.overview(projectId),
        (old) => {
          if (!old) return;

          return {
            ...old,
            projectById: {
              ...old.projectById,
              customer: old.projectById.customer
                ? {
                    ...old.projectById.customer,
                    contacts:
                      old.projectById.customer?.contacts.map((contact) => {
                        if (contact.id === variables.customerContactId) {
                          return {
                            ...contact,
                            name: variables.name,
                            description: variables.description,
                            email: variables.email,
                            phone: variables.phone,
                            isPrimary: variables.isPrimary || contact.isPrimary,
                          };
                        } else {
                          return contact;
                        }
                      }) || [],
                  }
                : null,
            },
          };
        }
      );

      options?.onMutate?.(variables);

      // Return a context object with the snapshotted value
      return { previousProjectOverview };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        projectKeys.overview(projectId),
        // @ts-expect-error
        context.previousProjectOverview
      );

      options?.onError?.(err, newTodo, context);
    },
    // Always refetch after error or success:
    onSettled: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.overview(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: complianceKeys.formPrefill(projectId),
      });
      // The Estimate recipient picker reads customer contacts from the
      // estimates query, so it must refresh when a contact is edited.
      void queryClient.invalidateQueries({
        queryKey: projectKeys.estimates(projectId),
      });

      options?.onSettled?.(...args);
    },
  });
};

export default useOptimisticUpdateCustomerContactOnProject;
